import {
  Users2, Flame, CheckCircle2, CalendarClock, AlertTriangle, IndianRupee,
  CalendarDays, HeartPulse, TrendingUp, PhoneCall, CalendarCheck, CalendarRange,
  CalendarPlus, UserCheck,
} from "lucide-react";
import { formatCurrency } from "@/lib/constants";

/* ── Widget catalog: KPI cards + charts available on the dashboard ──────────
 * Scope matters when labelling these. Most tiles cover the selected period
 * (default: current IST month). Three are LIVE and ignore it — Hot Leads,
 * Active Patients, Missed Follow-ups — and the Today / This Month tiles are
 * fixed to those windows by design. Keep the word "Today"/"This Month" in a
 * label whenever the tile ignores the picker, so it can't be mistaken for a
 * windowed number. See routes_analytics.dashboard() for the authoritative split.
 */
export const WIDGET_CATALOG = {
  total_leads: {
    label: "Total Leads", type: "kpi", icon: Users2,
    visible: (show) => true,
    build: (k) => ({ value: k.total_leads }),
  },
  hot_leads: {
    label: "Hot Leads (Live)", type: "kpi", icon: Flame, accent: "gold",
    visible: (show) => true,
    build: (k) => ({ value: k.hot_leads }),
  },
  conversion_rate: {
    label: "Conversion Rate", type: "kpi", icon: TrendingUp, accent: "gold",
    visible: (show) => true,
    build: (k) => ({ value: `${k.conversion_rate}%` }),
  },
  revenue_or_converted: {
    label: "Revenue / Converted", type: "kpi",
    visible: (show) => true,
    build: (k, show) => show.revenue
      ? { value: formatCurrency(k.revenue), icon: IndianRupee, label: "Revenue" }
      : { value: k.converted, icon: CheckCircle2, label: "Converted" },
  },
  converted: {
    label: "Converted", type: "kpi", icon: CheckCircle2,
    visible: (show) => true,
    build: (k) => ({ value: k.converted }),
  },
  revenue: {
    label: "Revenue", type: "kpi", icon: IndianRupee,
    visible: (show) => show.revenue,
    build: (k) => ({ value: formatCurrency(k.revenue) }),
  },
  todays_followups: {
    label: "Today's Follow-ups", type: "kpi", icon: CalendarClock,
    visible: (show) => true,
    build: (k) => ({ value: k.todays_followups }),
  },
  missed_followups: {
    // Live: the whole overdue backlog as of now, not "went overdue in the period".
    label: "Missed Follow-ups (Live)", type: "kpi", icon: AlertTriangle, accent: "gold",
    visible: (show) => true,
    build: (k) => ({ value: k.missed_followups }),
  },
  appointments: {
    // Appointments HAPPENING in the period (scheduled_at).
    label: "Appointments", type: "kpi", icon: CalendarDays,
    visible: (show) => true,
    build: (k) => ({ value: k.appointments }),
  },
  appointments_booked: {
    // Appointments BOOKED in the period (created_at) — sales output, not clinic load.
    label: "Appointments Booked", type: "kpi", icon: CalendarPlus,
    visible: (show) => true,
    build: (k) => ({ value: k.appointments_booked ?? 0 }),
  },
  visited: {
    // Visits that HAPPENED in the period (scheduled_at + the terminal `visited`
    // status) — the same measure Branch Overview's Visited column and the Revenue
    // tile use, so all three reconcile for one window.
    label: "Visited", type: "kpi", icon: UserCheck,
    visible: (show) => true,
    build: (k) => ({ value: k.visited ?? 0 }),
  },
  appointments_today: {
    label: "Appointments Today", type: "kpi", icon: CalendarCheck, accent: "gold",
    visible: (show) => true,
    build: (k) => ({ value: k.appointments_today ?? 0 }),
  },
  appointments_month: {
    label: "Appointments This Month", type: "kpi", icon: CalendarRange,
    visible: (show) => true,
    build: (k) => ({ value: k.appointments_month ?? 0 }),
  },
  patients: {
    // Live: the current roster, not patients registered in the period.
    label: "Active Patients (Live)", type: "kpi", icon: HeartPulse,
    visible: (show) => true,
    build: (k) => ({ value: k.patients }),
  },
  calls_today: {
    label: "Calls Today", type: "kpi", icon: PhoneCall,
    visible: (show) => true,
    build: (k) => ({ value: k.calls_today ?? 0 }),
  },
  funnel_chart: {
    label: "Conversion Funnel", type: "chart",
    visible: (show) => show.funnel,
  },
  sources_chart: {
    label: "Lead Sources", type: "chart",
    visible: (show) => show.sources,
  },
  branch_performance: {
    label: "Branch Performance", type: "chart",
    visible: (show) => show.branch_performance,
  },
  branch_overview: {
    label: "Branch Overview (Leads / Booked / Visited / Revenue)", type: "chart",
    visible: (show) => show.branch_overview,
  },
  call_activity_table: {
    label: "Call Activity (All Users)", type: "chart",
    visible: (show) => show.call_activity,
  },
  leads_today_by_source: {
    label: "Leads Created Today (By Source)", type: "chart",
    visible: (show) => show.leads_today_by_source,
  },
  leads_by_status: {
    label: "Leads By Status", type: "chart",
    visible: (show) => show.leads_by_status,
  },
  lead_count_by_owner: {
    label: "Lead Count By Owner", type: "chart",
    visible: (show) => show.lead_count_by_owner,
  },
  leads_by_source_all: {
    // Key kept for saved layouts; scope is now the selected period, not all-time.
    label: "Leads By Source", type: "chart",
    visible: (show) => show.leads_by_source_all,
  },
  appointments_trend: {
    // scheduled_at — when appointments HAPPEN. Its twin below counts when they were
    // booked; the label spells out which, since the numbers differ.
    label: "Appointments Trend (Appointment Date)", type: "chart",
    visible: (show) => show.appointments_trend,
  },
  appointments_booked_trend: {
    label: "Appointments Booked Trend (Date Created)", type: "chart",
    visible: (show) => show.appointments_booked_trend,
  },
  appointments_by_status: {
    label: "Appointments By Status", type: "chart",
    visible: (show) => show.appointments_by_status,
  },
};

export const DEFAULT_WIDGET_LAYOUT = [
  "total_leads", "hot_leads", "conversion_rate", "revenue_or_converted",
  "todays_followups", "missed_followups",
  "appointments_today", "appointments_month", "appointments",
  "appointments_booked", "visited", "patients",
  "funnel_chart", "sources_chart", "branch_performance", "branch_overview",
  "appointments_trend", "appointments_booked_trend", "appointments_by_status",
];
